import {useEffect} from 'react';

/**
 * How far bottom-right floating UI must move left to clear the open right-hand
 * drawers, published as a CSS custom property on the document root.
 *
 * A custom property rather than React context on purpose. The one consumer is the
 * support-chat launcher, which is fixed to the viewport's bottom-right corner and
 * rendered far outside any drawer's tree — it has no provider in common with the
 * drawers it needs to avoid, and giving it one would mean threading a provider
 * around the whole app to move a button 640px. The launcher already coordinates
 * with the DOM this way, injecting a stylesheet to shift the Zendesk iframe.
 *
 * There is one property and there can be several drawers, so every open drawer
 * is tracked and the property holds the widest open width. Closing one drawer
 * while another is still open leaves the other's width in place; the property is
 * removed only when no drawer is open. A drawer that unmounts while open (a route
 * change mid-flight) is forgotten too, so it cannot leave the launcher parked off
 * to one side.
 *
 * Every right-hand drawer in the app needs this. RightDrawer calls it for its
 * consumers; a drawer built without RightDrawer has to call it itself.
 */
export const RIGHT_DRAWER_OFFSET_PROPERTY = '--right-drawer-offset';

// The width of each open drawer, keyed by a token unique to one effect run.
const openDrawerWidths = new Map<symbol, number>();

const publishOffset = (): void => {
	const root = document.documentElement;
	if (openDrawerWidths.size === 0) {
		root.style.removeProperty(RIGHT_DRAWER_OFFSET_PROPERTY);
		return;
	}
	root.style.setProperty(RIGHT_DRAWER_OFFSET_PROPERTY, `${Math.max(...openDrawerWidths.values())}px`);
};

export const useRightDrawerOffset = (isOpen: boolean, width: number): void => {
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}
		const drawer = Symbol('right-drawer');
		openDrawerWidths.set(drawer, width);
		publishOffset();
		return () => {
			openDrawerWidths.delete(drawer);
			publishOffset();
		};
	}, [isOpen, width]);
};
